import type { AuditAction } from '@/api/generated';

/**
 * What each audit code means, in words.
 *
 * Typed against the backend's published enum, so a code added there fails the
 * build here until it has a phrase. Written in the past tense, because every
 * entry is something that has already happened.
 */
export const ACTION_PHRASES: Record<AuditAction, string> = {
  'organization.created': 'Created the organization',
  'organization.updated': 'Updated the organization’s account',
  'organization.status_changed': 'Changed the organization’s status',
  'organization.profile_updated': 'Updated the profile',
  'organization.logo_updated': 'Changed the logo',
  'organization.cover_updated': 'Changed the cover image',
  'organization.billing_contact_updated': 'Changed the billing contact',
  'invitation.sent': 'Sent an invitation',
  'invitation.resent': 'Resent an invitation',
  'invitation.revoked': 'Revoked an invitation',
  'invitation.accepted': 'Accepted an invitation',
  'user.role_changed': 'Changed a user’s role',
  'user.removed': 'Removed a user',
  'supporter.started': 'Started supporting the organization',
  'supporter.ended': 'Stopped supporting the organization',
  'supporter.notifications_changed': 'Changed their notification settings',
  'member.notifications_changed': 'Changed their notification settings',
  'arc.details_updated': 'Updated the ARC’s details',
  'arc.header_updated': 'Updated the ARC’s header',
  'arc.section_created': 'Added a section to the ARC',
  'arc.section_updated': 'Changed a section of the ARC',
  'arc.section_deleted': 'Deleted a section of the ARC',
  'arc.sections_reordered': 'Reordered the ARC’s sections',
  'arc.item_added': 'Added content to the ARC',
  'arc.item_removed': 'Removed content from the ARC',
  'arc.items_reordered': 'Reordered content in the ARC',
  'arc.published': 'Published the ARC',
  'arc.unpublished': 'Unpublished the ARC',
  'arc.draft_saved': 'Saved the ARC draft',
  // The lead developer's campaigns module (merged October 2026).
  'campaign.created': 'Created a campaign',
  'campaign.updated': 'Updated a campaign',
  'campaign.status_changed': 'Changed a campaign’s status',
  // The document library (October 2026). The scan rejection is written by the
  // system with no actor; the backend's open question on its wording is
  // settled here.
  'document.created': 'Uploaded a document',
  'document.updated': 'Updated a document',
  'document.status_changed': 'Changed a document’s status',
  'document.file_replaced': 'Replaced a document’s file',
  'document.scan_rejected': 'Document rejected by the malware scan',
  'document_category.created': 'Created a document category',
  'document_category.updated': 'Updated a document category',
  'document_category.deleted': 'Deleted a document category',
  'recommended_page.added': 'Added a recommended page',
  'recommended_page.removed': 'Removed a recommended page',
  'recommended_page.reordered': 'Reordered the recommended pages',
  // The fundraising and volunteer links (October 2026). Setting, changing the
  // mode and removing are one `updated` code.
  'link.updated': 'Updated a link',
  'link.published': 'Published a link',
  'link.unpublished': 'Unpublished a link',
  // Custom pages (informational-pages, October 2026); a link change is a
  // `page.updated` with a `links` field.
  'page.created': 'Created a custom page',
  'page.updated': 'Updated a custom page',
  'page.status_changed': 'Changed a custom page’s status',
};

/** Every code, in the order the backend publishes them, for the filter. */
export const ACTION_CODES = Object.keys(ACTION_PHRASES) as AuditAction[];

/**
 * The phrase for a code, or the code itself.
 *
 * An entry's `action` is a plain string: a backend deployed ahead of the portal
 * can send a code the portal has never met. Showing it as it came is honest; a
 * blank or a guess would not be.
 */
export function actionPhrase(code: string): string {
  return (ACTION_PHRASES as Record<string, string>)[code] ?? code;
}
